import { legacyHttpClient } from "@services/http/legacyHttpClient";
import type { ApiResponse } from "@/types";
import type { BackendTicket, Ticket } from "@/types/ticket.types";

/** Spanish month names → month index (1-based). */
const MONTH_MAP: Record<string, number> = {
  ENERO: 1, FEBRERO: 2, MARZO: 3, ABRIL: 4, MAYO: 5, JUNIO: 6,
  JULIO: 7, AGOSTO: 8, SEPTIEMBRE: 9, OCTUBRE: 10, NOVIEMBRE: 11, DICIEMBRE: 12,
};

/**
 * Extrae el año de fechaCompra ("DD-MM-YYYY HH:MM:SS").
 * Retorna el año actual como fallback si el formato no coincide.
 */
function extractPurchaseYear(fechaCompra: string | undefined): number {
  if (!fechaCompra) return new Date().getFullYear();
  const match = fechaCompra.match(/^\d{2}-\d{2}-(\d{4})/);
  const year = match ? parseInt(match[1]!, 10) : NaN;
  return year >= 2020 ? year : new Date().getFullYear();
}

/**
 * Parsea fechaCompra ("DD-MM-YYYY HH:MM:SS") a Date.
 * Retorna inicio del año de compra como fallback.
 */
function parsePurchaseDate(fechaCompra: string | undefined, purchaseYear: number): Date {
  if (!fechaCompra) return new Date(purchaseYear, 0, 1);
  const m = fechaCompra.match(/^(\d{2})-(\d{2})-(\d{4})[T\s](.+)$/);
  if (m) {
    const d = new Date(`${m[3]}-${m[2]}-${m[1]}T${m[4]}`);
    if (!isNaN(d.getTime())) return d;
  }
  return new Date(purchaseYear, 0, 1);
}

/**
 * Parse a Spanish date string like "JUEVES 07 DE MAYO" or "VIERNES 08 MAYO"
 * into an ISO date string. Ancla al año de compra (fechaCompra) en lugar del
 * año actual — evita que eventos de Nov/Dic 2025 se clasifiquen como Nov/Dic 2026.
 * Returns null if the string cannot be parsed.
 */
function parseSpanishDate(dateStr: string | undefined, fechaCompra?: string): string | null {
  if (!dateStr) return null;
  const parts = dateStr.toUpperCase().replace(" DE ", " ").split(" ").filter(Boolean);
  // parts may be: ["JUEVES","07","MAYO"] or ["07","MAYO"] etc.
  let day: number | undefined;
  let month: number | undefined;
  for (const part of parts) {
    const asNum = parseInt(part, 10);
    if (!isNaN(asNum) && asNum >= 1 && asNum <= 31) {
      day = asNum;
    } else if (MONTH_MAP[part]) {
      month = MONTH_MAP[part];
    }
  }
  if (!day || !month) return null;

  const purchaseYear = extractPurchaseYear(fechaCompra);
  const purchaseDate = parsePurchaseDate(fechaCompra, purchaseYear);
  const candidate = new Date(purchaseYear, month - 1, day);

  // Los eventos ocurren después de la compra. Si la fecha candidata
  // es anterior a la compra, el evento es del año siguiente.
  if (candidate < purchaseDate) {
    return new Date(purchaseYear + 1, month - 1, day).toISOString();
  }
  return candidate.toISOString();
}

/**
 * Map the legacy backend ticket shape to the frontend Ticket model.
 * Mirrors what myevents.js accessed as item.saleTemp.* fields.
 */
function mapBackendTicketToTicket(raw: BackendTicket, index: number): Ticket {
  // `raw.qr` IS the scannable ticket code (backend: item.getCode().getCode()) —
  // the exact value the gate scanner matches (`where code like ...`) and the same
  // value the PDF encodes via QRCode.from(code). It must go into the QR verbatim.
  // Decoding it (atob) produced a DIFFERENT string the scanner could not read.
  // See bug-20260619-frontend-qr-atob. Do NOT re-introduce atob here.
  const qrData = raw.qr ?? "";
  const qr = `https://api.qrserver.com/v1/create-qr-code/?size=245x245&data=${encodeURIComponent(qrData)}`;

  return {
    id: raw.saleTemp.id ?? String(index),
    eventId: String(raw.saleTemp.identificador ?? ""),
    eventName: raw.saleTemp.evento,
    purchaseDate: raw.saleTemp.fechaCompra,
    localityName: raw.saleTemp.detail,
    quantity: raw.saleTemp.quantity,
    code: raw.numeroBoletos,
    qr,
    image: raw.saleTemp.image ? encodeURI(raw.saleTemp.image) : null,
    eventDateIso: parseSpanishDate(raw.saleTemp.date, raw.saleTemp.fechaCompra),
  };
}

/**
 * Ticket service — fetches purchased tickets from the legacy Java backend.
 */
export const ticketService = {
  /**
   * Fetch all tickets for a user by email.
   * Equivalent of myevents.js: POST /get_event_by_client_id with { email }.
   * legacyHttpClient handles the double-parsed { status, message, data: string } envelope.
   */
  async getMyTickets(email: string): Promise<ApiResponse<Ticket[]>> {
    try {
      const raw = await legacyHttpClient.post<BackendTicket[]>(
        "/api/web/get_event_by_client_id",
        { email },
      );

      const tickets = Array.isArray(raw)
        ? raw.map(mapBackendTicketToTicket)
        : [];

      return { success: true, data: tickets };
    } catch (error) {
      return {
        success: false,
        data: [],
        message:
          error instanceof Error
            ? error.message
            : "Error al cargar tus tickets",
      };
    }
  },
};